import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MessageModel } from '@core/models/message.model';
import * as fromRoot from '@core/redux';
import { StoreMessageService } from '@core/services/store-message.service';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';

import { TaskViewCommentItemComponent } from '../task-view-comment-item/task-view-comment-item.component';

interface CommentsState {
  messageListHide: MessageModel[];
  messageList: MessageModel[];
  messageListPinned: MessageModel[];
  isShowMoreMessage: boolean;
}

@Component({
  selector: 'app-task-view-comments',
  templateUrl: './task-view-comments.component.html',
  styleUrls: ['./task-view-comments.component.less'],
  standalone: true,
  imports: [TaskViewCommentItemComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskViewCommentsComponent implements OnInit {
  private readonly store = inject<Store<fromRoot.State>>(Store);
  private readonly storeMessage = inject(StoreMessageService);
  private readonly destroyRef = inject(DestroyRef);
  @Output() newMessageEmitter = new EventEmitter<void>();

  @Input() newMessageSubject!: Subject<MessageModel>;

  private readonly state = signal<CommentsState>({
    messageListHide: [],
    messageList: [],
    messageListPinned: [],
    isShowMoreMessage: false,
  });

  protected readonly messageListHide = computed(() => this.state().messageListHide);
  protected readonly messageList = computed(() => this.state().messageList);
  protected readonly messageListPinned = computed(() => this.state().messageListPinned);
  protected readonly isShowMoreMessage = computed(() => this.state().isShowMoreMessage);

  protected trackByMessageId(index: number, message: MessageModel): string {
    return message.id;
  }

  @Input()
  set taskId(taskId: string) {
    if (taskId) {
      this.storeMessage
        .getMessagesByTaskId(taskId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(messages => {
          this.state.update(state => ({
            ...state,
            messageList: messages.messageList,
            messageListHide: messages.messageListHide,
            messageListPinned: messages.messageListPinned,
          }));
        });
    }
  }

  ngOnInit(): void {
    this.newMessageSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(message => {
      this.state.update(state => {
        const newMessageList = [...state.messageList];
        const newMessageListHide = [...state.messageListHide];
        if (newMessageList.length >= this.storeMessage.limitVisible) {
          const removedMessage = newMessageList.splice(0, 1)[0];
          newMessageListHide.push(removedMessage);
        }

        newMessageList.push(message);

        return {
          ...state,
          messageList: newMessageList,
          messageListHide: newMessageListHide,
        };
      });

      setTimeout(() => {
        this.newMessageEmitter.emit();
      });
    });
  }

  showMoreMessage(): void {
    this.state.update(state => ({
      ...state,
      isShowMoreMessage: true,
    }));
  }

  hideMoreMessage(): void {
    this.state.update(state => ({
      ...state,
      isShowMoreMessage: false,
    }));
  }
}
